import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

export interface TraceDescriptionToolStat {
  name: string;
  count: number;
  error_count: number;
  mocked: boolean;
  stop: boolean;
  total_duration_ms: number;
}

export interface TraceDescriptionSlowestCall {
  span_id: string;
  tool: string;
  duration_ms: number;
}

export interface TraceDescriptionError {
  span_id: string;
  span_name: string;
  message: string | null;
}

export interface TraceDescriptionStats {
  status: "success" | "error" | "running";
  tools: TraceDescriptionToolStat[];
  slowest_call: TraceDescriptionSlowestCall | null;
  errors: TraceDescriptionError[];
  error_count: number;
  total_tokens: number;
  total_price: number;
}

interface TraceDescriptionResponse {
  success: boolean;
  description?: string | null;
  span_count?: number | null;
  stats?: TraceDescriptionStats | null;
  current_span_count?: number;
  message?: string;
}

const traceDescriptionUrl = (traceId: string, workspaceId?: number) => {
  const url = new URL(`${ENDPOINTS.trace}/${traceId}/description`);
  if (workspaceId) {
    url.searchParams.set("workspace_id", workspaceId.toString());
  }
  return url.toString();
};

export const useTraceDescription = ({
  userToken,
  traceId,
  workspaceId,
}: {
  userToken: string;
  traceId?: string;
  workspaceId?: number;
}) =>
  useQuery<TraceDescriptionResponse, Error>(
    [ENDPOINTS.trace, traceId, workspaceId, "description"],
    async () => {
      const response = await fetch(traceDescriptionUrl(traceId!, workspaceId), {
        headers: authHeader(userToken),
      });

      return handleResponse(response);
    },
    {
      enabled: Boolean(traceId && workspaceId),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

export const useGenerateTraceDescription = ({
  userToken,
  traceId,
  workspaceId,
}: {
  userToken: string;
  traceId?: string;
  workspaceId?: number;
}) => {
  const queryClient = useQueryClient();

  return useMutation<TraceDescriptionResponse, Error, void>(
    async () => {
      const response = await fetch(traceDescriptionUrl(traceId!, workspaceId), {
        method: "POST",
        headers: authHeader(userToken),
      });

      return handleResponse(response);
    },
    {
      onSuccess: data => {
        queryClient.setQueryData([ENDPOINTS.trace, traceId, workspaceId, "description"], data);
      },
    },
  );
};
